console.log('[DEBUG] dataStore.ts loaded'); // Changed from dataStore.js
import { defineStore } from 'pinia';
import { useSupabaseClient } from '#imports'; // Nuxt 3 auto-imports
// import { useNuxtApp } from '#app'; // Commented out - module not found 
import type { Database } from '~/types/supabase'; // Import Database type
import type { FormSchema } from '~/layers/adme-blob-cms/types/forms'; // For form elements if needed

// Define interfaces for state items for better type safety
// These are examples; they should ideally match your actual data structures or Supabase table rows
export interface Image {
  id: string;
  url: string;
  name?: string;
  alt_text?: string;
  category?: string;
  tags?: string[];
  is_bookmarked?: boolean | null;
  dealerships?: { name: string }; // Example, adjust if 'dealerships' is a direct property or fetched differently
  [key: string]: any;
}

interface Company {
  id: string;
  name: string;
  [key: string]: any;
}

interface Dealership {
  id: string;
  name: string;
  [key: string]: any;
}

interface Page {
  id: string;
  title: string;
  status?: string;
  dealerships?: { name: string };
  [key: string]: any;
}

interface Form { // This should align with forms table Row from Database type
  id: string;
  title: string | null;
  description?: string | null;
  status?: string | null;
  dealership_id?: string | null;
  schema?: FormSchema | null; // Assuming schema is parsed FormSchema
  dealerships?: { name: string }; // If joined
  created_at?: string | null;
  updated_at?: string | null;
  [key: string]: any;
}

interface SiteMenu {
  id: string;
  menu_name: string;
  dealership_id: string;
  dealerships?: { name: string };
  [key: string]: any;
}

interface TeamMember {
  id: string;
  name: string;
  dealership_id: string;
  dealerships?: { name: string };
  [key: string]: any;
}

interface Post {
  id: string;
  title: string | null;
  dealership_id: string;
  dealerships?: { name: string };
  [key: string]: any;
}

interface UserData {
  email: string;
  first_name: string;
  roles: { name: string }; // This might be more complex
  user_dealerships: Dealership[]; // Assuming this structure from fetchUserData
  [key: string]: any;
}

interface SliderItem {
  id: string;
  title?: string | null;
  [key: string]: any;
}


export const useDataStore = defineStore('dataStore', {
  state: () => ({
    images: [] as Image[],
    companies: [] as Company[],
    dealerships: [] as Dealership[],
    pages: [] as Page[],
    forms: [] as Form[], 
    siteMenus: [] as SiteMenu[],
    teamMembers: [] as TeamMember[],
    posts: [] as Post[],
    currentPage: 1,
    pageSize: 10,
    totalPages: 0,
    totalItems: 0,
    currentTab: 'all',
    userDealershipIds: [] as string[], 
    searchQuery: '',
    userData: null as UserData | null,
    lastFetchedCompanies: null as number | null,
    lastFetchedPages: null as number | null,
    lastFetchedForms: null as number | null, 
    lastFetchedSiteMenus: null as number | null,
    lastFetchedTeamMembers: null as number | null,
    lastFetchedPosts: null as number | null,
    categories: [] as string[], 
    allTags: [] as string[], 
    selectedDealershipId: null as string | null, 
    sliderItems: [] as SliderItem[],
    currentSliderItemPage: 1,
    sliderItemPageSize: 10, 
    totalSliderItems: 0,
    totalSliderItemPages: 0,
    bookmarkedImages: [] as Image[],
  }),
  getters: {
    selectedDealership(state): Dealership | undefined {
      return state.dealerships.find(dealer => dealer.id === state.selectedDealershipId);
    },
    getSelectedDealershipId(state): string | null {
      return state.selectedDealershipId;
    }
  },
  actions: {
    async fetchCategoriesAndTags(dealershipId: string | null = null) {
      console.log(`[dataStore] fetchCategoriesAndTags called for dealershipId: ${dealershipId}`);
      try {
        const supabase = useSupabaseClient<Database>();
        let query = supabase.from('images').select('category, tags');

        if (dealershipId) {
          query = query.eq('dealership_id', dealershipId);
          console.log(`[dataStore] fetchCategoriesAndTags: Applying dealership_id filter: ${dealershipId}`);
        } else {
          console.log('[dataStore] fetchCategoriesAndTags: No dealership_id provided, fetching global categories/tags.');
        }

        const { data, error } = await query;

        if (error) {
          console.error('Error fetching categories and tags directly from Supabase:', error.message, error);
          this.categories = [];
          this.allTags = [];
          return;
        }
    
        if (data) {
          const categoriesSet = new Set<string>(); 
          const tagsSet = new Set<string>(); 
    
          data.forEach(item => { // item is now typed based on images table
            if (item.category && typeof item.category === 'string') {
              categoriesSet.add(item.category.trim());
            }
            if (Array.isArray(item.tags)) {
              item.tags.forEach(tag => {
                if (tag && typeof tag === 'string') {
                  tagsSet.add(tag.trim());
                }
              });
            }
          });
    
          this.categories = Array.from(categoriesSet).filter(Boolean).sort();
          this.allTags = Array.from(tagsSet).filter(Boolean).sort();
          console.log(`[dataStore] Categories populated: ${this.categories.length}, Tags populated: ${this.allTags.length}`);
        } else {
          this.categories = [];
          this.allTags = [];
        }
      } catch (error: any) {
        console.error('Error in client-side fetchCategoriesAndTags:', error.message, error);
        this.categories = [];
        this.allTags = [];
      }
    },    
    async deleteImage(image: Image) {
        console.log(`[dataStore] deleteImage called for image ID: ${image.id}`);
        try {
            const supabase = useSupabaseClient<Database>();
            const nuxtApp = useNuxtApp();
            const supabaseUrl = nuxtApp.$config.public.supabase.url;

            // 1. Delete from Supabase Storage
            // Extract the storage path from the full URL
            const storagePath = image.url.replace(`${supabaseUrl}/storage/v1/object/public/`, '');
            console.log(`[dataStore] Deleting from storage at path: ${storagePath}`);

            const { error: storageError } = await supabase.storage.from('images').remove([storagePath]);

            if (storageError) {
                // Log the error but don't necessarily stop, as the DB record might be orphaned.
                // Or, you could return here if you want to be stricter.
                console.error('Error deleting image from Supabase Storage:', storageError.message, storageError);
                // Depending on requirements, you might want to throw an error here.
                // For now, we'll proceed to attempt DB deletion.
            } else {
                console.log(`[dataStore] Successfully deleted image from storage.`);
            }

            // 2. Delete from Supabase Database (images table)
            const { error: dbError } = await supabase.from('images').delete().eq('id', image.id);

            if (dbError) {
                console.error('Error deleting image from database:', dbError.message, dbError);
                throw new Error('Failed to delete image from the database.');
            } else {
                console.log(`[dataStore] Successfully deleted image record from the database.`);
            }

            // 3. Remove from local state
            const index = this.images.findIndex(i => i.id === image.id);
            if (index !== -1) {
                this.images.splice(index, 1);
                console.log(`[dataStore] Removed image from local state.`);
            }

            return true; // Indicate success
        } catch (error: any) {
            console.error('An unexpected error occurred in deleteImage:', error.message, error);
            return false; // Indicate failure
        }
    },
    async updateImageDetails(imageId: string, details: { category?: string; tags: string[] }) {
        console.log(`[dataStore] updateImageDetails called for image ID: ${imageId}`);
        try {
            const supabase = useSupabaseClient<Database>();

            const updateData: { category?: string; tags: string[] } = {
                tags: details.tags,
            };

            if (details.category !== undefined) {
                updateData.category = details.category;
            }

            const { data, error } = await supabase
                .from('images')
                .update(updateData)
                .eq('id', imageId)
                .select()
                .single();

            if (error) {
                console.error('Error updating image details in database:', error.message, error);
                throw new Error('Failed to update image details.');
            }

            // Update the local state
            const index = this.images.findIndex(i => i.id === imageId);
            if (index !== -1 && data) {
                this.images[index] = { ...this.images[index], ...(data as any) };
                console.log(`[dataStore] Updated image in local state.`);
            }

            return true;
        } catch (error: any) {
            console.error('An unexpected error occurred in updateImageDetails:', error.message, error);
            return false;
        }
    },
    async fetchImages(
      page: number = 1, 
      pageSize: number = 10, 
      status: string = 'all', // status not used for images query
      searchQuery: string = '', 
      dealershipId: string | null = null, 
      selectedCategories: string[] = [], 
      tags: string[] = [], 
      dateRange: { start?: string | Date, end?: string | Date } = {}
    ) {
      console.log('[dataStore] fetchImages called with params:', { page, pageSize, status, searchQuery, dealershipId, selectedCategories, tags, dateRange });

      this.currentPage = page;
      this.pageSize = pageSize;
      try {
        const supabase = useSupabaseClient<Database>();
        const from = (page - 1) * pageSize;
        const to = from + pageSize - 1;

        let query = supabase
          .from('images')
          .select('*, dealerships(name)', { count: 'exact' });

        if (dealershipId) {
          query = query.eq('dealership_id', dealershipId);
        }

        if (searchQuery) {
          query = query.textSearch('fts', searchQuery, { type: 'websearch' });
          console.log(`[dataStore] Applied search query: ${searchQuery}`);
        }

        if (selectedCategories && selectedCategories.length > 0) {
          query = query.in('category', selectedCategories);
          console.log(`[dataStore] Applied category filter:`, selectedCategories);
        }

        if (tags && tags.length > 0) {
          query = query.contains('tags', tags);
          console.log(`[dataStore] Applied tags filter:`, tags);
        }

        if (dateRange.start) {
          query = query.gte('created_at', dateRange.start instanceof Date ? dateRange.start.toISOString() : dateRange.start);
        }

        if (dateRange.end) {
          query = query.lte('created_at', dateRange.end instanceof Date ? dateRange.end.toISOString() : dateRange.end);
          console.log(`[dataStore] Applied date range end:`, dateRange.end);
        }

        // Add ordering and pagination back
        query = query.order('created_at', { ascending: false }).range(from, to);

        const { data, error, count } = await query;

        console.log('[dataStore] Supabase query executed. Error:', error);
        console.log('[dataStore] Supabase query executed. Count:', count);

        if (error) {
          console.error('Error fetching images:', error.message, error);
          this.images = [];
          this.totalItems = 0;
          this.totalPages = 0;
          return;
        }
        
        const nuxtApp = useNuxtApp();
        const supabasePublicUrl = nuxtApp.$config.public.supabase.url;

        const transformedData = data?.map((image) => { // data can be null
          const dbUrl = image.url;
          let finalPublicUrl = dbUrl;
          // ... (URL transformation logic remains, ensure image properties are correctly typed)
          return { ...image, url: finalPublicUrl };
        }) || [];

        this.images = transformedData as Image[]; // Cast if necessary, ensure Image type matches
        this.totalPages = Math.ceil((count || 0) / pageSize);
        this.totalItems = count || 0;

      } catch (error: any) {
        console.error('Error in client-side fetchImages:', error.message, error);
        this.images = [];
        this.totalItems = 0;
        this.totalPages = 0;
      }
    },      
    subscribeToImages(dealershipId: string) {
      const supabase = useSupabaseClient<Database>(); 
      if (!supabase || typeof supabase.from !== 'function') {
        console.error("Supabase client not initialized correctly for subscription.");
        return; 
      }
      supabase
        .channel(`images-dealership-${dealershipId}`) 
        .on('postgres_changes', { event: '*', schema: 'public', table: 'images', filter: `dealership_id=eq.${dealershipId}` }, (payload: any) => { // payload should be typed
          this.handleRealtimeChange(payload);
        })
        .subscribe();
    },
    handleRealtimeChange(payload: any) { // payload should be SupabaseRealtimePayload<Image> or similar
      const { eventType, new: newImage, old: oldImage } = payload;
      switch (eventType) {
        case 'INSERT':
          this.images.unshift(newImage as Image);
          break;
        case 'UPDATE':
          const index = this.images.findIndex(image => image.id === (newImage as Image).id);
          if (index !== -1) {
            this.images.splice(index, 1, newImage as Image);
          }
          break;
        case 'DELETE':
          this.images = this.images.filter(image => image.id !== (oldImage as {id: string}).id); 
          break;
      }
      this.totalItems = this.images.length;
      this.totalPages = Math.ceil(this.totalItems / this.pageSize);
    },
    async fetchForms(page: number = 1, pageSize: number = 10, status: string = 'all', searchQuery: string = '', dealershipId: string | null = null) {
      try {
        // API call, response type can be defined if API route is typed
        const response = await fetch(`/api/forms?page=${page}&pageSize=${pageSize}&status=${status}&searchQuery=${searchQuery}&dealership_id=${dealershipId || ''}`);
        const result = await response.json();
        if (!response.ok) { throw new Error(result.error || 'Failed to fetch forms'); }
        const { data, count } = result; // data should be Form[]
        this.forms = (data as Form[]).map(form => ({ ...form, dealerships: form.dealerships || { name: 'Unknown' } }));
        this.currentPage = page;
        this.totalPages = Math.ceil(count / pageSize);
        this.totalItems = count;
        this.lastFetchedForms = new Date().getTime();
      } catch (error: any) {
        console.error('Error fetching forms:', error.message);
      }
    },    
    async fetchPosts(page: number = 1, pageSize: number = 10, status: string = 'all', searchQuery: string = '', dealershipId: string | null = null) {
      try {
        // API call
        const response: { data: Post[], count: number } = await $fetch(`/api/posts?page=${page}&pageSize=${pageSize}&status=${status}&searchQuery=${searchQuery}&dealership_id=${dealershipId || ''}`);
        const { data, count } = response;
        if (Array.isArray(data)) {
          this.posts = data.filter(post => this.userDealershipIds.includes(post.dealership_id)).map(post => ({ ...post, dealerships: post.dealerships || { name: 'Unknown' } }));
          this.totalItems = count || 0;
        } else {
          this.posts = []; this.totalItems = 0;
        }
        this.currentPage = page;
        this.totalPages = Math.ceil(this.totalItems / pageSize);
        this.lastFetchedPosts = new Date().getTime();
      } catch (error: any) {
        console.error('Error fetching posts:', error.message); this.posts = []; this.totalItems = 0; this.totalPages = 0;
      }
    },
    async fetchPostById(id: string): Promise<{ data: Post | null, error: string | null }> {
      try {
        // API call
        const response = await $fetch<{ data: Post }>(`/api/posts/${id}`); // Assuming API returns { data: Post }
        return { data: response.data, error: null };
      } catch (error: any) {
        console.error('Error fetching post:', error.message); return { data: null, error: error.message };
      }
    },
    async fetchSiteMenus(page: number = 1, pageSize: number = 10, status: string = 'all', searchQuery: string = '', dealershipId: string | null = null) {
      try {
        // If no specific dealership is provided, use the user's first dealership ID
        const targetDealershipId = dealershipId || (this.userDealershipIds.length > 0 ? this.userDealershipIds[0] : null);
        
        // API call
        const response: { data: SiteMenu[], count: number } = await $fetch(`/api/site_menus?page=${page}&pageSize=${pageSize}&status=${status}&searchQuery=${searchQuery}&dealership_id=${targetDealershipId || ''}`);
        const { data, count } = response; 
        if (Array.isArray(data)) {
          // Don't filter here since the API already filters by dealership_id
          this.siteMenus = data.map(menu => ({ ...menu, dealerships: menu.dealerships || { name: 'Unknown' } }));
          this.totalItems = count || 0; 
        } else {
          this.siteMenus = []; this.totalItems = 0;
        }
        this.currentPage = page;
        this.totalPages = Math.ceil(this.totalItems / pageSize);
        this.lastFetchedSiteMenus = new Date().getTime();
      } catch (error: any) {
        console.error('Error fetching site menus:', error.message); this.siteMenus = []; this.totalItems = 0; this.totalPages = 0;
      }
    },
    setSiteMenus(data: SiteMenu[], count: number) {
      this.siteMenus = data.map(menu => ({ ...menu, dealerships: menu.dealerships || { name: 'Unknown' } }));
      this.totalItems = count; this.totalPages = Math.ceil(count / this.pageSize);
    },
    async fetchSiteMenuById(id: string): Promise<{ data: SiteMenu | null, error: string | null }> {
      try {
        // API call
        const response = await $fetch<{ data: SiteMenu }>(`/api/site_menus/${id}`);
        return { data: response.data, error: null };
      } catch (error: any) {
        console.error('Error fetching site menu:', error.message); return { data: null, error: error.message };
      }
    },
    async fetchTeamMembers(page: number = 1, pageSize: number = 10, status: string = 'all', searchQuery: string = '', dealershipId: string | null = null) {
      try {
        // API call
        const response: { data: TeamMember[], count: number } = await $fetch(`/api/team_members?page=${page}&pageSize=${pageSize}&status=${status}&searchQuery=${searchQuery}&dealership_id=${dealershipId || ''}`);
        const { data, count } = response;
        if (Array.isArray(data)) {
          this.teamMembers = data.filter(member => this.userDealershipIds.includes(member.dealership_id)).map(member => ({ ...member, dealerships: member.dealerships || { name: 'Unknown' } }));
          this.totalItems = count || 0;
        } else {
          this.teamMembers = []; this.totalItems = 0;
        }
        this.currentPage = page;
        this.totalPages = Math.ceil(this.totalItems / pageSize);
        this.lastFetchedTeamMembers = new Date().getTime();
      } catch (error: any) {
        console.error('Error fetching team members:', error.message); this.teamMembers = []; this.totalItems = 0; this.totalPages = 0;
      }
    },
    setTeamMembers(data: TeamMember[], count: number) {
      this.teamMembers = data.map(member => ({ ...member, dealerships: member.dealerships || { name: 'Unknown' } }));
      this.totalItems = count; this.totalPages = Math.ceil(count / this.pageSize);
    },
    async fetchTeamMemberById(id: string): Promise<{ data: TeamMember | null, error: string | null }> {
      try {
        // API call
        const response = await $fetch<{ data: TeamMember }>(`/api/team_members/${id}`);
        return { data: response.data, error: null }; // Assuming API returns { data: ... }
      } catch (error: any) {
        console.error('Error fetching team member:', error.message); return { data: null, error: error.message };
      }
    },
    async createTeamMember(memberData: Partial<TeamMember>): Promise<{ data: TeamMember | null, error: string | null }> {
      try {
        // API call
        const response = await $fetch<{ data: TeamMember }>('/api/team_members', {
          method: 'POST',
          body: memberData
        });
        return { data: response.data, error: null };
      } catch (error: any) {
        console.error('Error creating team member:', error.message);
        return { data: null, error: error.message };
      }
    },
    async createPost(postData: Partial<Post>): Promise<{ data: Post | null, error: string | null }> {
      try {
        // API call
        const response = await $fetch<{ data: Post }>('/api/posts', {
          method: 'POST',
          body: postData
        });
        return { data: response.data, error: null };
      } catch (error: any) {
        console.error('Error creating post:', error.message);
        return { data: null, error: error.message };
      }
    },
    async createSiteMenu(menuData: Partial<SiteMenu>): Promise<{ data: SiteMenu | null, error: string | null }> {
      try {
        // API call
        const response = await $fetch<{ data: SiteMenu }>('/api/site_menus', {
          method: 'POST',
          body: menuData
        });
        return { data: response.data, error: null };
      } catch (error: any) {
        console.error('Error creating site menu:', error.message);
        return { data: null, error: error.message };
      }
    },
    async fetchPages(page: number = 1, pageSize: number = 10, status: string = 'all', searchQuery: string = '', dealershipId: string | null = null) {
      try {
        const searchParamsObject: Record<string, string> = { 
          page: String(page), 
          pageSize: String(pageSize), 
          status: status, 
          searchQuery: searchQuery
        };
        if (dealershipId !== null) {
          searchParamsObject.dealership_id = dealershipId;
        }
        const queryParams = new URLSearchParams(searchParamsObject);
        // API call
        const response = await fetch(`/api/pages?${queryParams.toString()}`);
        const result = await response.json(); // Define type for result if possible
        if (!response.ok) { throw new Error(result.error || 'Failed to fetch pages'); }
        const { data, count } = result; // data should be Page[]
        this.pages = (data as Page[]).map(p => ({ ...p, dealerships: p.dealerships || { name: 'Unknown' } }));
        this.currentPage = page;
        this.totalPages = Math.ceil(count / pageSize);
        this.totalItems = count;
        this.lastFetchedPages = new Date().getTime();
      } catch (error: any) {
        console.error('Error fetching pages:', error.message);
      }
    },
    async fetchFormById(id: string): Promise<{ data: Form | null, error: string | null }> { 
      try {
        // API call
        const response = await fetch(`/api/forms/${id}`); 
        const result = await response.json(); // Define type for result if possible
        if (!response.ok) { 
          console.error('Error fetching form by id:', result.error || response.statusText);
          throw new Error(result.error || `Error fetching form ${id}`); 
        }
        return { data: (result.data || result) as Form, error: null }; 
      } catch (error: any) {
        console.error('Error in fetchFormById:', error.message); 
        return { data: null, error: error.message };
      }
    },
    setPages(data: Page[], count: number) {
      this.pages = data.map(p => ({ ...p, dealerships: p.dealerships || { name: 'Unknown' } }));
      this.totalItems = count; this.totalPages = Math.ceil(count / this.pageSize);
    },
    async updatePageStatus(pageId: string, newStatus: string) {
      const supabase = useSupabaseClient<Database>(); 
      // Assuming 'pages' table has 'status' column. Types will confirm.
      const { data, error } = await supabase.from('pages').update({ status: newStatus }).eq('id', pageId).select().single();
      if (error) { console.error('Error updating page status:', error.message); return null; }
      return data; // data will be the updated page record or null
    },
    async fetchPageById(id: string): Promise<{ data: Page | null, error: string | null }> {
      try {
        // API call
        const response = await fetch(`/api/pages/${id}`);
        const result = await response.json(); // Define type for result if possible
        if (!response.ok) { throw new Error(result.error || 'Failed to fetch page'); }
        return { data: result.data as Page, error: null };
      } catch (error: any) {
        console.error('Error fetching page:', error.message); return { data: null, error: error.message };
      }
    },
    changeTab(status: string) { this.currentTab = status; },
    setSearchQuery(query: string) { this.searchQuery = query; },
    async fetchAllPages(): Promise<Page[]> {
      const supabase = useSupabaseClient<Database>(); 
      const now = new Date().getTime();
      if (this.lastFetchedPages && now - this.lastFetchedPages < 5 * 60 * 1000) { return this.pages; }
      // Types will ensure select string is valid and data is typed
      const { data, error } = await supabase.from('pages').select(`*, dealerships (name)`).order('id', { ascending: true });
      if (error) { console.error(error); return []; }
      this.pages = data as Page[]; this.lastFetchedPages = now; return data as Page[];
    },
    async fetchUserData(userEmail: string) {
      try {
        // API call
        const result = await $fetch<{data: UserData}>(`/api/user?email=${userEmail}`);
        if (result.data) {
          this.userData = result.data;
          this.dealerships = result.data.user_dealerships; 
          this.userDealershipIds = result.data.user_dealerships.map(d => d.id);
          if (Array.isArray(this.userDealershipIds) && this.userDealershipIds.length > 0) {
            this.selectedDealershipId = this.userDealershipIds[0] || null; 
          } else {
            this.selectedDealershipId = null; 
          }
        } else {
          console.error('No user data found for email:', userEmail);
        }
      } catch (error: any) {
        console.error('Error fetching user data:', error.message);
      }
    },
    async logout() {
      const supabase = useSupabaseClient<Database>(); 
      try {
        const { error } = await supabase.auth.signOut();
        if (error) { console.error("Error during sign out:", error.message); }
      } catch (e: any) {
        console.error("Exception during sign out process:", e.message);
      }
      this.userData = null; this.userDealershipIds = []; this.selectedDealershipId = null;
    },
    setUserDealershipIds(dealershipIds: string[]) { this.userDealershipIds = dealershipIds; },

    async fetchSliderItems(options: { page?: number, pageSize?: number, dealershipId?: string | null, status?: string } = {}) {
      const { 
        page = 1, 
        pageSize = this.sliderItemPageSize, 
        dealershipId = null, 
        status = 'active'
      } = options;

      try {
        const params: Record<string, string> = { 
          page: String(page), 
          pageSize: String(pageSize),
        };
        if (dealershipId) {
          params.dealership_id = dealershipId;
        }
        if (status && status !== 'all') {
          params.status = status;
        }
        
        const queryParams = new URLSearchParams(params);
        // API call
        const response = await fetch(`/api/slider-items?${queryParams.toString()}`);
        const result = await response.json(); // Define type for result if possible

        if (!response.ok) {
          const errorPayload = result && result.error ? result.error : `HTTP error ${response.status}`;
          throw new Error(String(errorPayload));
        }

        const { data, count } = result; // data should be SliderItem[]
        this.sliderItems = data as SliderItem[]; 
        this.currentSliderItemPage = page;
        this.totalSliderItems = count || 0;
        this.totalSliderItemPages = Math.ceil((count || 0) / pageSize);
      } catch (error: any) {
        console.error('Error fetching slider items:', error.message);
        this.sliderItems = [];
        this.totalSliderItems = 0;
        this.totalSliderItemPages = 0;
      }
    },
    async addSliderItem(itemData: Partial<SliderItem>): Promise<SliderItem | null> { // Assuming itemData is partial for insert
      try {
        // API call
        const response = await fetch('/api/slider-items', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(itemData),
        });
        const result = await response.json(); // Define type for result if possible

        if (!response.ok) {
          const errorPayload = result && result.error ? result.error : `HTTP error ${response.status}`;
          throw new Error(String(errorPayload));
        }
        return result.data as SliderItem; // Assuming API returns { data: SliderItem }
      } catch (error: any) {
        console.error('Error adding slider item:', error.message);
        throw error; 
      }
    },
    async toggleImageBookmark(image: Image) {
      console.log(`[dataStore] toggleImageBookmark called for image ID: ${image.id}, current status: ${image.is_bookmarked}`);
      const newStatus = !image.is_bookmarked;
      
      try {
        const supabase = useSupabaseClient<Database>();
        const { data, error } = await supabase
          .from('images')
          .update({ is_bookmarked: newStatus })
          .eq('id', image.id)
          .select()
          .single();

        if (error) {
          console.error('Error updating bookmark status in database:', error.message, error);
          throw new Error('Failed to update bookmark status.');
        }

        console.log('[dataStore] Bookmark status updated successfully in DB.');

        // Optimistically update local state
        const localImage = this.images.find(i => i.id === image.id);
        if (localImage) {
          localImage.is_bookmarked = newStatus;
        }
        
        // Also update the bookmarkedImages list
        if (newStatus) {
            this.bookmarkedImages.push({ ...image, is_bookmarked: true });
        } else {
            const index = this.bookmarkedImages.findIndex(i => i.id === image.id);
            if (index !== -1) {
                this.bookmarkedImages.splice(index, 1);
            }
        }
        
        return data;
      } catch (error: any) {
        console.error('An unexpected error occurred in toggleImageBookmark:', error.message, error);
        return null;
      }
    },
    async fetchBookmarkedImages() {
      console.log('[dataStore] fetchBookmarkedImages called.');
      try {
        const supabase = useSupabaseClient<Database>();
        const { data, error } = await supabase
          .from('images')
          .select('*, dealerships(name)')
          .eq('is_bookmarked', true)
          .order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching bookmarked images:', error.message, error);
          this.bookmarkedImages = [];
          return;
        }

        this.bookmarkedImages = data as Image[];
        console.log(`[dataStore] Fetched ${data.length} bookmarked images.`);
      } catch (error: any) {
        console.error('An unexpected error occurred in fetchBookmarkedImages:', error.message, error);
        this.bookmarkedImages = [];
      }
    },
    async fetchSingleImage(imageId: string) {
      console.log(`[dataStore] fetchSingleImage called for image ID: ${imageId}`);
      try {
        const supabase = useSupabaseClient<Database>();
        const { data, error } = await supabase
          .from('images')
          .select('*, dealerships(name)')
          .eq('id', imageId)
          .single();

        if (error) {
          console.error(`Error fetching single image with ID ${imageId}:`, error.message, error);
          this.images = [];
          this.totalItems = 0;
          this.totalPages = 0;
          return;
        }

        if (data) {
          this.images = [data as Image];
          this.totalItems = 1;
          this.currentPage = 1;
          this.totalPages = 1;
          console.log(`[dataStore] Successfully fetched single image.`);
        } else {
          this.images = [];
          this.totalItems = 0;
          this.totalPages = 0;
        }
      } catch (error: any) {
        console.error('An unexpected error occurred in fetchSingleImage:', error.message, error);
        this.images = [];
        this.totalItems = 0;
        this.totalPages = 0;
      }
    },
  },
});
